import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';

interface SettingsData {
  aktiflik_bedeli: number;
  kayit_primi: number;
  seviye1_kazanc: number;
  seviye2_kazanc: number;
  seviye3_kazanc: number;
  seviye4_kazanc: number;
  seviye5_kazanc: number;
  seviye6_kazanc: number;
}

interface AyarlarProps {
  settings: {
    aktiflikBedeli: number;
    kayitPrimi: number;
    seviye1Kazanc: number;
    seviye2Kazanc: number;
    seviye3Kazanc: number;
    seviye4Kazanc: number;
    seviye5Kazanc: number;
    seviye6Kazanc: number;
  };
  onSettingsChange: (s: AyarlarProps['settings']) => void;
}

export default function Ayarlar({ settings, onSettingsChange }: AyarlarProps) {
  const { profile } = useAuth();
  const [localSettings, setLocalSettings] = useState(settings);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notifs, setNotifs] = useState({ aktiflik: true, whatsapp: true, risk: false });

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    setError('');

    const { error: saveError } = await supabase
      .from('step6_settings')
      .update({
        aktiflik_bedeli: localSettings.aktiflikBedeli,
        kayit_primi: localSettings.kayitPrimi,
        seviye1_kazanc: localSettings.seviye1Kazanc,
        seviye2_kazanc: localSettings.seviye2Kazanc,
        seviye3_kazanc: localSettings.seviye3Kazanc,
        seviye4_kazanc: localSettings.seviye4Kazanc,
        seviye5_kazanc: localSettings.seviye5Kazanc,
        seviye6_kazanc: localSettings.seviye6Kazanc,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', profile.id);

    if (saveError) {
      setError('Kaydedilirken bir hata oluştu. Lütfen tekrar dene.');
      setSaving(false);
      return;
    }

    onSettingsChange(localSettings);
    setSaved(true);
    setSaving(false);
    setTimeout(() => setSaved(false), 2500);
  };

  const fields = [
    { label: 'Aylık Aktiflik Bedeli', key: 'aktiflikBedeli' as const, icon: 'ri-shopping-bag-3-line', color: 'text-red-400', desc: 'Her ay ödenecek ürün bedeli' },
    { label: 'Kayıt Primi (kişi başı)', key: 'kayitPrimi' as const, icon: 'ri-user-add-line', color: 'text-green-400', desc: 'Yeni kayıt başına kazanılan prim' },
    { label: '1. Seviye Kazanç', key: 'seviye1Kazanc' as const, icon: 'ri-arrow-right-circle-line', color: 'text-amber-400', desc: 'Direkt ekip üyesi başına aylık kazanç' },
    { label: '2. Seviye Kazanç', key: 'seviye2Kazanc' as const, icon: 'ri-arrow-right-circle-line', color: 'text-sky-400', desc: '2. jenerasyon üye başına aylık kazanç' },
    { label: '3. Seviye Kazanç', key: 'seviye3Kazanc' as const, icon: 'ri-arrow-right-circle-line', color: 'text-purple-400', desc: '3. jenerasyon üye başına aylık kazanç' },
    { label: '4. Seviye Kazanç', key: 'seviye4Kazanc' as const, icon: 'ri-arrow-right-circle-line', color: 'text-rose-400', desc: '4. jenerasyon üye başına aylık kazanç' },
    { label: '5. Seviye Kazanç', key: 'seviye5Kazanc' as const, icon: 'ri-arrow-right-circle-line', color: 'text-emerald-400', desc: '5. jenerasyon üye başına aylık kazanç' },
    { label: '6. Seviye Kazanç', key: 'seviye6Kazanc' as const, icon: 'ri-arrow-right-circle-line', color: 'text-orange-400', desc: '6. jenerasyon üye başına aylık kazanç' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Ayarlar</h1>
        <p className="text-sm text-gray-500 mt-1">Kazanç planını ve sistem tercihlerini yapılandır</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compensation Plan */}
        <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
              <i className="ri-money-dollar-circle-line text-amber-400 text-base" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">Kazanç Planı</h2>
              <p className="text-xs text-gray-500">Şirket planını bu alandan gir</p>
            </div>
          </div>

          <div className="space-y-3">
            {fields.map(field => (
              <div key={field.key} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`${field.icon} text-sm ${field.color}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white">{field.label}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{field.desc}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-sm text-gray-500">₺</span>
                    <input
                      type="number"
                      value={localSettings[field.key]}
                      onChange={e => setLocalSettings({ ...localSettings, [field.key]: Number(e.target.value) })}
                      className="w-24 rounded-lg px-3 py-1.5 text-sm text-right text-white font-semibold outline-none text-sm"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div className="mt-4 rounded-xl px-3 py-2.5 text-xs" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <p className="text-red-400">{error}</p>
            </div>
          )}

          <button onClick={handleSave} disabled={saving}
            className="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white cursor-pointer transition-all hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
            style={{ background: saved ? 'rgba(34,197,94,0.2)' : 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)', border: saved ? '1px solid rgba(34,197,94,0.3)' : 'none' }}>
            {saving ? (
              <>
                <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Kaydediliyor...
              </>
            ) : saved ? (
              <>
                <i className="ri-check-line text-green-400 text-base" />
                Plan Kaydedildi!
              </>
            ) : (
              <>
                <i className="ri-calculator-line text-base" />
                Planı Oluştur
              </>
            )}
          </button>
        </div>

        {/* System Preferences */}
        <div className="space-y-4">
          {/* Profile Info */}
          <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,180,255,0.12)' }}>
                <i className="ri-shield-user-line text-blue-400 text-base" />
              </div>
              <h2 className="text-sm font-bold text-white">Hesap Bilgileri</h2>
            </div>
            <div className="space-y-3">
              <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <div className="text-xs text-gray-500 mb-1">Ad Soyad</div>
                <div className="text-sm font-semibold text-white">{profile?.full_name || '—'}</div>
              </div>
              <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <div className="text-xs text-gray-500 mb-1">Rol</div>
                <div className="text-sm font-semibold text-amber-400">
                  {profile?.role === 'kullanici' ? 'Kullanıcı' : profile?.role === 'lider' ? 'Lider' : profile?.role === 'admin' ? 'Admin' : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.12)' }}>
                <i className="ri-notification-3-line text-purple-400 text-base" />
              </div>
              <h2 className="text-sm font-bold text-white">Bildirimler</h2>
            </div>
            <div className="space-y-3">
              {[
                { key: 'aktiflik' as const, label: 'Aktiflik Hatırlatmaları', desc: 'Ay sonu öncesi uyarı' },
                { key: 'whatsapp' as const, label: 'WhatsApp Önerileri', desc: 'Günlük aksiyonlar' },
                { key: 'risk' as const, label: 'Risk Uyarıları', desc: 'Kritik durumlarda bildirim' },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div>
                    <div className="text-sm font-medium text-white">{item.label}</div>
                    <div className="text-xs text-gray-600 mt-0.5">{item.desc}</div>
                  </div>
                  <button onClick={() => setNotifs(n => ({ ...n, [item.key]: !n[item.key] }))}
                    className="relative w-10 h-5 rounded-full cursor-pointer transition-all flex-shrink-0"
                    style={{ background: notifs[item.key] ? 'linear-gradient(135deg, #F5A623, #FF6B35)' : 'rgba(255,255,255,0.1)' }}>
                    <span className="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all"
                      style={{ left: notifs[item.key] ? 'calc(100% - 1.1rem)' : '2px' }} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* App Info */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(245,166,35,0.04)', border: '1px solid rgba(245,166,35,0.12)' }}>
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/bd011a02-ab2e-435b-9429-0067149db8ff.png"
                alt="STEP6" className="w-10 h-10 rounded-xl object-contain" />
              <div>
                <div className="text-sm font-bold text-white">STEP6 Network</div>
                <div className="text-xs text-amber-400">Ekip Yönetim Paneli v1.0</div>
                <div className="text-xs text-gray-600 mt-0.5">© 2026 STEP6 Network</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}